import { Checkbox, Divider, Tooltip } from 'antd';
import Collapse from 'antd/lib/collapse';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import {
  ArrowLeftSvg,
  CopySvg,
  CrossSvg,
  DeleteSvg,
  PencilSvg,
} from '../../../../assets/icons';
import ArrowRightSvg from '../../../../assets/icons/ArrowRightSvg';
import EditSvg from '../../../../assets/icons/EditSvg';
import FullScreenSvg from '../../../../assets/icons/FullScreenSvg';
import MindmapSvg from '../../../../assets/icons/MindmapSvg';
import MinimizeSvg from '../../../../assets/icons/MinimizeSvg';
import MoreSvg from '../../../../assets/icons/MoreSvg';
import RichTextDataInputSvg from '../../../../assets/icons/RichtTextDataInputSvg';
import theme from '../../../../assets/theme';
import {
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import {
  Prompt,
  PromptDropdownOption,
  PromptInput,
  PromptInputType,
} from '../../../../services/backendService/types';
import {
  isGenerativeImagePrompt,
  isGenerativeTextPrompt,
  MAX_LIMIT_LEARNING_OBJECTIVE,
  MAX_LIMIT_PROMPT,
  MAX_LIMIT_UPLOADEDTEXT,
} from '../../../../utils/aiGeneratorUtils';
import { getIndexToColor } from '../../../../utils/creator';
import { notUndefined } from '../../../../utils/general';
import { zIndexes } from '../../../../utils/zIndexes';
import Select from '../../../_atoms/_Select/Select';
import { Button } from '../../../_atoms/Button';
import EditInputOptionsModal from '../../../_atoms/EditInputOptionsModal';
import ExpandingButton from '../../../_atoms/ExpandingButton';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import SearchInput from '../../../_molecules/SearchInput';
import { parseSlateStringToText } from '../../../SlideComponents/slateUtil';
import { TabContainer } from '../../DesignerTabs/DesignerTabUtils';
import {
  createExtraLongTextPromptInput,
  createGradeDropdownPromptInput,
  createPromptUniqueId,
  createSlidesUploadPromptInput,
  NewPromptTemplate,
} from './newPromptTemplates';
import PromptBuilder, { findDependencies } from './PromptBuilder';
import PromptDependencyGraphModal from './PromptDependencyGraphModal';
import PromptImageBuilder from './PromptImageBuilder';

const PromptListPanelHeader = ({
  isEditing,
  promptName,
  promptId,
  onPromptNameChanged,
  onSelectPrompt,
}: {
  isEditing?: boolean;
  promptName: string;
  onSelectPrompt: (promptId: string) => void;
  promptId: string;
  onPromptNameChanged: (promptId: string, newName: string) => void;
}) => {
  const [name, setName] = useState<string>(promptName);

  if (isEditing) {
    return (
      <div style={{ width: '100%' }}>
        <SearchInput
          value={name}
          onPressEnter={() => onPromptNameChanged(promptId, name)}
          onChange={e => setName(e.replace(':', ''))} //we don't want char ':' in prompt name as we split by it
          icon={null}
          style={{ width: '100%' }}
          onBlur={() => onPromptNameChanged(promptId, name)}
          autoFocus
        ></SearchInput>
      </div>
    );
  }
  return (
    <PanelTitle
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onSelectPrompt(promptId);
      }}
    >
      <span>{promptName}</span>
    </PanelTitle>
  );
};

const PromptCollapseOverview = ({
  prompt,
  promptInput = [],
}: {
  prompt: Prompt;
  promptInput?: PromptInput[];
}) => {
  const userMessage = useMemo(() => {
    if (prompt.messages) {
      const userMessage = prompt.messages.find(m => m.role === 'user');
      if (userMessage) {
        const res = parseSlateStringToText(userMessage.content);
        return res || 'No user message';
      }
    }
  }, [prompt.messages]);

  const usedPromptInputs = useMemo(() => {
    const dependencies = findDependencies(prompt);
    const res = dependencies.map(dep => {
      const found = promptInput.find(p => p.id === dep.dependencyId);
      if (found) {
        return found;
      }
      return;
    });
    return res.filter(notUndefined);
  }, [prompt, promptInput]);
  return (
    <PromptCollapseOverviewContainer>
      <div style={{ gridColumn: '1/4' }}>
        {userMessage && userMessage.length > 50
          ? userMessage.slice(0, 50) + '...'
          : userMessage}
      </div>

      {usedPromptInputs.map((input, i) => (
        <div
          key={input.id}
          style={{ padding: '5px', borderLeft: `solid 1px ${getIndexToColor(i)}` }}
        >
          {input.label}
        </div>
      ))}
    </PromptCollapseOverviewContainer>
  );
};

type NewPromptForm = {
  promptName?: string;
  promptTemplate?: NewPromptTemplate;
  promptType: 'image' | 'text';
};

const PromptsTab = ({
  prompts,
  promptInputs,
}: {
  prompts?: Prompt[];
  promptInputs?: PromptInput[];
}) => {
  const { t } = useTranslation('Host');

  const localTemplateDispatch = useDesignerDataContext(c => c.dispatch);
  const showTabPaneFullWidth = useDesignerUIContext(c => c.state.showTabPaneFullWidth);
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const setSelectedPromptId = useDesignerUIContext(
    context => context.setSelectedPromptId,
  );
  const selectedElements = useDesignerUIContext(
    context => context.state.selectedElements,
  );

  const selectedElement = useMemo(() => {
    if (selectedElements?.length !== 1) return null;
    return selectedElements[0];
  }, [selectedElements]);

  const selectedPromptId = useDesignerUIContext(
    context => context.state.selectedPromptId,
  );
  const [showEditInputs, setShowEditInputs] = useState<boolean>(false);
  const [newPromptForm, setNewPromptForm] = useState<NewPromptForm | undefined>();
  const selectedPrompt = useMemo(() => prompts?.find(p => p.id === selectedPromptId), [
    prompts,
    selectedPromptId,
  ]);
  const [shouldShowEditOptionsModal, setShouldShowEditOptionsModal] = useState<
    string | undefined
  >(undefined);
  const [promptBeingEdited, setPromptBeingEdited] = useState<string>();
  const [localDropdownOptions, setLocalDropdownOptions] = useState<
    PromptDropdownOption[]
  >();
  const [isPromptDependencyModalVisible, setIsPromptDependencyModalVisible] = useState(
    false,
  );

  const handleNewPrompt = ({ promptName, promptTemplate, promptType }: NewPromptForm) => {
    const newPromptId = createPromptUniqueId();
    if (promptType === 'text') {
      localTemplateDispatch({
        type: TemplateStateActions.addPrompt,
        promptName: promptName ? promptName : `New ${promptTemplate} prompt`,
        newPromptTemplate: promptTemplate,
        newPromptId,
      });
    }
    if (promptType === 'image') {
      localTemplateDispatch({
        type: TemplateStateActions.addImagePrompt,
        promptName: promptName ? promptName : `New ${promptTemplate} image prompt`,
        newPromptTemplate: promptTemplate,
        newPromptId,
      });
    }
    setNewPromptForm(undefined);
    setSelectedPromptId(newPromptId);
  };
  const deletePrompt = (promptId: string) => {
    localTemplateDispatch({
      type: TemplateStateActions.deletePrompt,
      promptId: promptId,
    });
  };

  const handleDuplicatePrompt = (promptId: string) => {
    localTemplateDispatch({
      type: TemplateStateActions.duplicatePrompt,
      promptId: promptId,
    });
  };

  const handlePromptNameChanged = (promptId: string, newName: string) => {
    setPromptBeingEdited(undefined);
    const prompt = prompts?.find(p => p.id === promptId);
    if (!prompt) {
      return;
    }
    localTemplateDispatch({
      type: TemplateStateActions.editPrompt,
      prompt: {
        ...prompt,
        name: newName,
      },
    });
  };

  const handleRenamePromptStart = (promptId: string) => {
    setPromptBeingEdited(promptId);
  };

  const handleSelectPrompt = (promptId: string) => {
    setSelectedPromptId(prompts?.find(p => p.id === promptId)?.id);
    setNewPromptForm(undefined);
  };

  const validPromptInputOptions = useMemo(() => {
    /**
     * Can only have one grade input in a form. It will have the id: input-grade
     */
    const hasGrade = promptInputs?.some(
      input => input.id === 'input-grade' && input.type === 'dropdown',
    );
    const hasSlides = promptInputs?.some(input => input.type === 'slidesUpload');
    const baseOptions = [
      {
        displayName: 'Short text',
        value: 'shortText',
      },
      {
        displayName: 'Long text',
        value: 'longText',
      },
      {
        displayName: 'Big text',
        value: 'extraLongText',
      },
      {
        displayName: 'Passage',
        value: 'passage',
      },
      {
        displayName: 'Dropdown',
        value: 'dropdown',
      },
    ];

    if (!hasGrade) {
      baseOptions.push({
        displayName: 'Grade',
        value: 'grade',
      });
    }

    if (!hasSlides) {
      baseOptions.push({
        displayName: 'Slides',
        value: 'slidesUpload',
      });
    }
    return baseOptions;
  }, [promptInputs]);

  const handleAddPromptInput = () => {
    localTemplateDispatch({
      type: TemplateStateActions.addPromptInput,
    });
  };

  const handleDeletePromptInput = (inputId: string) => {
    localTemplateDispatch({
      type: TemplateStateActions.deletePromptInput,
      inputId,
    });
  };
  const handleEditPromptInput = (promptInput: PromptInput) => {
    const label = promptInput.label;
    const isRequired = promptInput.isRequired;
    const defaultValue = promptInput.defaultValue;
    localTemplateDispatch({
      type: TemplateStateActions.editPromptInput,
      promptInput: {
        ...promptInput,
        label,
        defaultValue,
        isRequired,
      },
    });
  };

  const handleBackButtonClicked = () => {
    setSelectedPromptId(undefined);
    if (showTabPaneFullWidth) {
      dispatch({
        type: DesignerUIStateActions.setShowTabPaneFullWidth,
        isExpanded: false,
      });
    }
  };

  useEffect(() => {
    // Upon promptInputs change, we want to reset the localDropdownOptions
    if (!shouldShowEditOptionsModal) {
      setLocalDropdownOptions(undefined);
    } else {
      const promptInput = promptInputs?.find(
        input => input.id === shouldShowEditOptionsModal,
      );
      if (promptInput?.constraints?.options) {
        setLocalDropdownOptions(promptInput.constraints.options || []);
      }
    }
  }, [promptInputs, shouldShowEditOptionsModal]);

  const handleEditOptions = useCallback(
    (inputId: string) => {
      const promptInput = promptInputs?.find(input => input.id === inputId);
      if (!promptInput) return;
      if (!localDropdownOptions) {
        // we remove all options
        localTemplateDispatch({
          type: TemplateStateActions.editPromptInput,
          promptInput: {
            ...promptInput,
            constraints: { options: undefined },
            defaultValue: undefined,
          },
        });
        return;
      }

      //overwrite all the options with new options
      localTemplateDispatch({
        type: TemplateStateActions.editPromptInput,
        promptInput: {
          ...promptInput,
          constraints: { options: localDropdownOptions },
          defaultValue: localDropdownOptions[0].value,
        },
      });
    },
    [localDropdownOptions, localTemplateDispatch, promptInputs],
  );

  const handleEditPromptInputType = ({
    newType,
    promptInput,
  }: {
    newType: PromptInputType | 'grade' | 'extraLongText';
    promptInput: PromptInput;
  }) => {
    if (newType === 'slidesUpload') {
      localTemplateDispatch({
        type: TemplateStateActions.editPromptInput,
        promptInput: {
          ...createSlidesUploadPromptInput({
            id: promptInput.id,
            label: 'Upload slides',
          }),
        },
      });
      return;
    }

    if (newType === 'grade' && promptInput.id !== 'input-grade') {
      // We create a new grade input
      localTemplateDispatch({
        type: TemplateStateActions.deletePromptInput,
        inputId: promptInput.id,
      });
      localTemplateDispatch({
        type: TemplateStateActions.addPromptInput,
        promptInput: { ...createGradeDropdownPromptInput() },
      });
      return;
    }
    // editing the grade field
    if (newType === 'grade' && promptInput.id === 'input-grade') {
      // We create a new grade input
      localTemplateDispatch({
        type: TemplateStateActions.editPromptInput,
        promptInput: { ...promptInput },
      });
      return;
    }
    if (newType === 'dropdown' && promptInput.id !== 'input-grade') {
      // We create a new dropdown input
      localTemplateDispatch({
        type: TemplateStateActions.editPromptInput,
        promptInput: {
          ...promptInput,
          type: newType as PromptInputType,
        },
      });
      return;
    }
    if (newType === 'extraLongText') {
      localTemplateDispatch({
        type: TemplateStateActions.editPromptInput,
        promptInput: {
          ...createExtraLongTextPromptInput(promptInput.label),
          id: promptInput.id,
        },
      });
      return;
    }

    if (newType === 'longText') {
      localTemplateDispatch({
        type: TemplateStateActions.editPromptInput,
        promptInput: {
          ...promptInput,
          type: newType,
          constraints: {
            ...promptInput.constraints,
            maxLength: MAX_LIMIT_LEARNING_OBJECTIVE,
          },
        },
      });
    }

    if (newType === 'shortText') {
      localTemplateDispatch({
        type: TemplateStateActions.editPromptInput,
        promptInput: {
          ...promptInput,
          type: newType,
          constraints: {
            ...promptInput.constraints,
            maxLength: MAX_LIMIT_PROMPT,
          },
        },
      });
    }

    localTemplateDispatch({
      type: TemplateStateActions.editPromptInput,
      promptInput: {
        ...promptInput,
        type: newType as PromptInputType,
      },
    });
  };

  const settingsItem = (prompt: Prompt) => {
    const dropdownOverlay = (
      <Menu
        onClick={e => {
          e.domEvent.preventDefault();
          e.domEvent.stopPropagation();
        }}
      >
        <Menu.Item
          key={'rename'}
          style={{ padding: 0 }}
          onClick={() => {
            handleRenamePromptStart(prompt.id);
          }}
        >
          <DropdownMenuItemButton>
            <PencilSvg /> {t('Rename')}
          </DropdownMenuItemButton>
        </Menu.Item>
        <Menu.Item
          key={'duplicate'}
          style={{ padding: 0 }}
          onClick={() => {
            handleDuplicatePrompt(prompt.id);
          }}
        >
          <DropdownMenuItemButton>
            <CopySvg /> {t('Duplicate')}
          </DropdownMenuItemButton>
        </Menu.Item>
        <Menu.Item
          key={'delete'}
          style={{ padding: 0 }}
          onClick={() => {
            deletePrompt(prompt.id);
          }}
        >
          <DropdownMenuItemButton style={{ color: theme.colors.red }}>
            <DeleteSvg /> {t('Delete')}
          </DropdownMenuItemButton>
        </Menu.Item>
      </Menu>
    );
    return (
      <Dropdown overlay={dropdownOverlay} trigger={['click']} placement="bottomRight">
        <IconWrapper
          onClick={event => {
            // If you don't want click extra trigger collapse, you can prevent this:
            event.stopPropagation();
          }}
          color={theme.colors.black}
          height="24px"
          width="24px"
        >
          <MoreSvg />
        </IconWrapper>
      </Dropdown>
    );
  };

  if (showEditInputs) {
    return (
      <TabContainer style={{ padding: '20px 0px 0px 0px', overflowY: 'hidden' }}>
        <BackButtonRowContainer onClick={() => setShowEditInputs(false)}>
          <IconWrapper height="30px" width="30px" color={theme.colors.black}>
            <ArrowLeftSvg />
          </IconWrapper>
          <Title $fontSize={20}>Back to prompt editing</Title>
        </BackButtonRowContainer>
        <div style={{ display: 'flex', margin: '0px 20px' }}>
          <Title $fontSize={18} style={{ marginBottom: 5, flex: 1 }}>
            Edit inputs
          </Title>
          <ExpandingButton
            buttonText={'Add new input'}
            handleClick={handleAddPromptInput}
          />
        </div>
        <EditInputsContainer>
          {(!promptInputs || promptInputs.length === 0) && (
            <p>No inputs yet, add a new input to get started.</p>
          )}
          <MessageContainer>
            {promptInputs?.map((input, i) => (
              <InputContainer key={input.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <InputTag>
                    <RichTextDataInputSvg />
                    {input.label}
                    <RemoveButton
                      onClick={() => handleDeletePromptInput(input.id)}
                      notext
                      icon={<CrossSvg />}
                    />
                  </InputTag>
                  {input.type !== 'dropdown' && input.type !== 'slidesUpload' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <p
                        style={{
                          marginBottom: 0,
                          fontSize: 12,
                          color: '#a5a5a5',
                          margin: '0 10px',
                        }}
                      >
                        Required?
                      </p>
                      <Checkbox
                        checked={input.isRequired}
                        onChange={val =>
                          handleEditPromptInput({
                            ...input,
                            isRequired: val.target.checked,
                          })
                        }
                      />
                    </span>
                  )}
                </div>
                <CustomDropdownContainer>
                  <div style={{ display: 'flex' }}>
                    <Select
                      label="Type"
                      onSelect={val =>
                        handleEditPromptInputType({
                          promptInput: input,
                          newType: val as PromptInputType & 'grade',
                        })
                      }
                      value={getPromptInputOption(input)}
                      options={validPromptInputOptions}
                    ></Select>
                    {input.type === 'dropdown' && input.label !== 'Grade' && (
                      <div style={{ marginTop: 10 }}>
                        <EditInputOptionsModal
                          title={input.label}
                          inputId={input.id}
                          visible={shouldShowEditOptionsModal}
                          onCancel={() => setShouldShowEditOptionsModal(undefined)}
                          onOk={() => {
                            handleEditOptions(input.id);
                            setShouldShowEditOptionsModal(undefined);
                          }}
                          setDropdownInputOptions={setLocalDropdownOptions}
                          numberOfOptions={localDropdownOptions?.length}
                          promptInputDropdownOptions={localDropdownOptions}
                        />
                      </div>
                    )}
                    {input.type === 'slidesUpload' && (
                      <div
                        style={{
                          paddingLeft: 5,
                          marginTop: 0,
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 3,
                        }}
                      >
                        <SearchInput
                          icon={null}
                          label={'Slides'}
                          value={input.constraints?.step?.toString()}
                          type="number"
                          onChange={val =>
                            handleEditPromptInput({
                              ...input,
                              constraints: {
                                ...input.constraints,
                                step: parseInt(val),
                              },
                            })
                          }
                          style={{ width: '100%' }}
                        />
                        <Button
                          height="38px"
                          width="32px"
                          styles={{ padding: 2, marginBottom: 1 }}
                          onClick={() => {
                            localTemplateDispatch({
                              type: TemplateStateActions.addCurifyPlaceholderSlides,
                              promptInputId: input.id,
                            });
                          }}
                        >
                          +
                        </Button>
                      </div>
                    )}
                  </div>
                  <SearchInput
                    icon={null}
                    label={'Label'}
                    value={input.label}
                    style={{ width: '100%' }}
                    onChange={val => handleEditPromptInput({ ...input, label: val })}
                  />
                </CustomDropdownContainer>
                {!input.isRequired && (
                  <div>
                    <SearchInput
                      icon={null}
                      value={input.defaultValue}
                      label={`Default ${input.label} if left blank by user`}
                      placeholder={`Add default ${input.label}`}
                      style={{ width: '100%' }}
                      onChange={val =>
                        handleEditPromptInput({ ...input, defaultValue: val })
                      }
                    />
                  </div>
                )}
                {input.type === 'dropdown' && input.label !== 'Grade' && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginLeft: 125,
                    }}
                  >
                    {input.constraints?.options && (
                      <>
                        <p
                          style={{ textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => setShouldShowEditOptionsModal(input.id)}
                        >
                          Edit options
                        </p>
                        <p>
                          {input.constraints.options.length} option
                          {input.constraints.options.length !== 1 ? 's' : ''} in list
                        </p>
                      </>
                    )}
                    {!input.constraints?.options && (
                      <p
                        style={{ textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={() => setShouldShowEditOptionsModal(input.id)}
                      >
                        add options
                      </p>
                    )}
                  </div>
                )}
                {i !== promptInputs.length - 1 && (
                  <Divider style={{ margin: '30px 0px 30px 0px' }} />
                )}
              </InputContainer>
            ))}
          </MessageContainer>
        </EditInputsContainer>
      </TabContainer>
    );
  }

  if (selectedPromptId && selectedPrompt && isGenerativeTextPrompt(selectedPrompt)) {
    return (
      <TabContainer style={{ padding: '20px 0px 0px 0px' }}>
        <CollapseButtonContainer
          style={{ position: 'absolute', top: 10, right: 10 }}
          onClick={() => {
            if (showTabPaneFullWidth) {
              dispatch({
                type: DesignerUIStateActions.setShowTabPaneFullWidth,
                isExpanded: false,
              });
            } else {
              dispatch({
                type: DesignerUIStateActions.setShowTabPaneFullWidth,
                isExpanded: true,
              });
            }
          }}
        >
          <Tooltip
            title={showTabPaneFullWidth ? 'Minimize prompt view' : 'Expand prompt view'}
          >
            <CollapseButton>
              <IconWrapper>
                {showTabPaneFullWidth ? <MinimizeSvg /> : <FullScreenSvg />}
              </IconWrapper>
            </CollapseButton>
          </Tooltip>
        </CollapseButtonContainer>

        <BackButtonRowContainer onClick={() => handleBackButtonClicked()}>
          <IconWrapper height="30px" width="30px" color={theme.colors.black}>
            <ArrowLeftSvg />
          </IconWrapper>
          <Title $fontSize={20}>{selectedPrompt.name}</Title>
        </BackButtonRowContainer>
        <SelectedPromptContentContainer style={{ height: 'calc(100% - 40px)' }}>
          <PromptBuilder
            prompt={selectedPrompt}
            localTemplateDispatch={localTemplateDispatch}
            promptInputs={promptInputs}
            setShowEditInputs={setShowEditInputs}
          />
        </SelectedPromptContentContainer>
      </TabContainer>
    );
  }
  if (selectedPromptId && selectedPrompt && isGenerativeImagePrompt(selectedPrompt)) {
    return (
      <TabContainer style={{ padding: '20px 0px 0px 0px' }}>
        <BackButtonRowContainer onClick={() => setSelectedPromptId(undefined)}>
          <IconWrapper height="30px" width="30px" color={theme.colors.black}>
            <ArrowLeftSvg />
          </IconWrapper>
          <Title $fontSize={20}>{selectedPrompt.name}</Title>
        </BackButtonRowContainer>
        <SelectedPromptContentContainer style={{ height: 'calc(100% - 40px)' }}>
          <PromptImageBuilder
            prompt={selectedPrompt}
            localTemplateDispatch={localTemplateDispatch}
            promptInputs={promptInputs}
            setShowEditInputs={setShowEditInputs}
          />
        </SelectedPromptContentContainer>
      </TabContainer>
    );
  }

  return (
    <TabContainer
      style={{
        padding: '20px 0px 0px 0px',
        display: 'grid',
        gridTemplateRows: '40px min-content 36px auto',
      }}
    >
      <PromptDependencyGraphModal
        visible={isPromptDependencyModalVisible}
        onCancel={() => setIsPromptDependencyModalVisible(false)}
      />
      <Title style={{ padding: '0px 20px' }} $fontSize={20}>
        {'Prompts'}
      </Title>
      <AvailableInputsContainer>
        <div style={{ display: 'flex' }}>
          <Title style={{ marginBottom: 0 }} $fontSize={16}>
            {'Available inputs'}
          </Title>
          <Button
            type="ghost"
            styles={{ border: 'none' }}
            notext
            icon={<EditSvg />}
            onClick={() => setShowEditInputs(true)}
          ></Button>
        </div>
        {(!promptInputs || promptInputs.length === 0) && (
          <p>No inputs yet, add a new prompt to get started.</p>
        )}
        {promptInputs && promptInputs.length >= 1 && (
          <InputTagsContainer>
            {promptInputs.map(input => (
              <InputTag key={input.id}>
                <RichTextDataInputSvg />
                {input.label}
              </InputTag>
            ))}
          </InputTagsContainer>
        )}
      </AvailableInputsContainer>
      <div style={{ padding: '0px 20px', display: 'flex', gap: '10px' }}>
        <Button
          width="100%"
          styles={{
            background: theme.colors.blue,
            fontSize: '14px',
            padding: '5px 0px',
          }}
          onClick={() =>
            setNewPromptForm({
              promptType: 'text',
              promptTemplate: 'empty' as NewPromptTemplate,
            })
          }
        >
          New text prompt
        </Button>
        <Button
          width="100%"
          onClick={() =>
            setNewPromptForm({
              promptType: 'image',
              promptTemplate: 'empty',
            })
          }
          styles={{ fontSize: '14px', padding: '5px 0px' }}
          type={'ghost'}
        >
          New image prompt
        </Button>
      </div>
      <PromptsListScrollingContainer>
        {prompts && (
          <Button
            type="ghost"
            onClick={() => setIsPromptDependencyModalVisible(true)}
            icon={<MindmapSvg />}
            styles={{ fontSize: '14px', padding: '5px', marginBottom: '10px' }}
          >
            {t('Prompt connections')}
          </Button>
        )}

        {!!newPromptForm && (
          <NewPromptFormContainer>
            <h2 style={{ margin: 0, fontSize: 16 }}>
              New {newPromptForm.promptType} prompt
            </h2>
            <SearchInput
              value={newPromptForm.promptName}
              label={'Name'}
              style={{ width: '100%' }}
              placeholder={'Write in your prompt name'}
              onChange={e => {
                setNewPromptForm({ ...newPromptForm, promptName: e });
              }}
              icon={null}
              autoFocus
            ></SearchInput>
            <Select
              label="Prompt template"
              onSelect={val => {
                setNewPromptForm({
                  ...newPromptForm,
                  promptTemplate: val as NewPromptTemplate,
                });
              }}
              value={newPromptForm.promptTemplate}
              options={
                newPromptForm.promptType === 'text'
                  ? [
                      {
                        displayName: 'Empty',
                        value: 'empty',
                      },
                      {
                        displayName: 'Brain break',
                        value: 'brainBreak',
                      },
                      {
                        displayName: 'Would you rather',
                        value: 'wouldYouRather',
                      },
                    ]
                  : [
                      {
                        displayName: 'Empty',
                        value: 'empty',
                      },
                      {
                        displayName: 'Negative prompt',
                        value: 'negativePrompt',
                      },
                    ]
              }
            ></Select>
            <Button onClick={() => handleNewPrompt(newPromptForm)}>Create</Button>
          </NewPromptFormContainer>
        )}
        <CollapseStyled
          expandIconPosition={'right'}
          expandIcon={({ isActive }) => (
            <StyledArrowIconWrapper height="24px" width="24px" $isActive={isActive}>
              <ArrowRightSvg />
            </StyledArrowIconWrapper>
          )}
        >
          {(prompts || []).map(prompt => (
            <CollapsePanel
              header={
                <PromptListPanelHeader
                  onPromptNameChanged={handlePromptNameChanged}
                  onSelectPrompt={handleSelectPrompt}
                  isEditing={promptBeingEdited === prompt.id}
                  promptId={prompt.id}
                  promptName={prompt.name}
                />
              }
              $isImagePrompt={isGenerativeImagePrompt(prompt)}
              key={prompt.id}
              style={{
                color: theme.colors.black,
                fontWeight: 'bold',
                background: selectedElement?.promptSourceId?.includes(prompt.id)
                  ? `${theme.colors.purple}25`
                  : theme.colors.white,
              }}
              extra={settingsItem(prompt)}
            >
              <PromptCollapseOverview prompt={prompt} promptInput={promptInputs} />
            </CollapsePanel>
          ))}
        </CollapseStyled>
      </PromptsListScrollingContainer>
    </TabContainer>
  );
};

const getPromptInputOption = (
  promptInput: PromptInput,
): PromptInputType | 'grade' | 'extraLongText' => {
  if (promptInput.type === 'longText') {
    if (promptInput.constraints?.maxLength === MAX_LIMIT_UPLOADEDTEXT) {
      return 'extraLongText';
    }
    return 'longText';
  }
  return promptInput.type;
};

const PanelTitle = styled.div`
  width: 100%;
`;
const PromptCollapseOverviewContainer = styled.div`
  display: grid;
  gap: 5px;
  grid-template-columns: 1fr 1fr 1fr;
`;
export default PromptsTab;

const CollapseButtonContainer = styled.div`
  padding: 0;
  border: none;
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 10px;
  z-index: ${zIndexes('CREATOR_LEFT_BAR_HIDE')};
  svg {
    padding: 0;
  }
`;

const CollapseButton = styled.div`
  background-color: white;
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 10px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  svg {
    padding: 0;
    width: 20px !important;
    height: 20px !important;
  }
`;

const SelectedPromptContentContainer = styled.div`
  height: calc(100% - 50px);
  overflow-y: auto;
  padding: 5px;
`;

const BackButtonRowContainer = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  padding: 0px 20px;
`;

const AvailableInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 20px 20px 20px;
`;

const EditInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 20px 20px 20px;
  height: calc(100% - 70px);
  overflow-y: auto;
`;

const InputTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const InputTag = styled.div`
  position: relative;
  padding: 3px 20px 3px 10px;
  vertical-align: baseline;
  border-radius: 4px;
  background-color: ${theme.colors.orange};
  font-size: 16px;
  font-weight: bold;
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  width: max-content;

  button {
    display: none;
  }

  svg {
    margin-right: 5px;
    height: 16px;
    width: 16px;
  }

  &:hover {
    button {
      display: flex;
    }
`;

const MessageContainer = styled.div`
  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StyledArrowIconWrapper = styled(IconWrapper)<{ $isActive?: boolean }>`
  ${({ $isActive }) =>
    $isActive &&
    css`
      && svg {
        transform: rotate(90deg);
      }
    `}
`;

const DropdownMenuItemButton = styled.button`
  background: none;
  border: none;
  padding: 5px 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-weight: bold;
  &:disabled {
    cursor: not-allowed;
  }
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
    margin-left: 5px;
    height: 16px;
    width: 16px;
  }
`;

const CollapsePanel = styled(Collapse.Panel)<{ $isImagePrompt?: boolean }>`
  ${({ $isImagePrompt }) =>
    $isImagePrompt
      ? css`
          span::before {
            content: '🖼 ';
          }
        `
      : css`
          span::before {
            content: '📝 ';
          }
        `}
`;
const CollapseStyled = styled(Collapse)`
  background-color: transparent;
  border: none;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: ${theme.effects.floatingCreatorElementBorderRadius};

  .ant-collapse-arrow {
    height: 24px;
    display: flex;
    align-items: center;
  }

  .ant-collapse-item {
    border-bottom: none;
  }
  .ant-collapse-content {
    border-top: none;
  }
  .ant-collapse-extra {
    position: absolute;
    top: 50%;
    right: 40px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    height: 100%;
  }
  .ant-collapse-header {
    padding-right: 70px !important;
  }
  .ant-collapse-content-box {
    /* padding: 0; */
  }
`;

const PromptsListScrollingContainer = styled.div`
  overflow-y: auto;
  height: 100%;
  padding: 20px;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;
  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;
const NewPromptFormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: 10px;
  gap: 10px;
  background: ${theme.colors.white};
  border-radius: ${theme.effects.floatingCreatorElementBorderRadius};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
`;

const RemoveButton = styled(Button)`
  position: absolute;
  right: -12.5px;
  top: 2.5px;
  border-radius: 50px;
  color: ${theme.colors.white};
  background-color: ${theme.colors.greyDark};
  width: 25px;
  height: 25px;
  opacity: 0.9;
  svg {
    margin: 0px;
    height: 10px;
    width: 10px;
  }

  &:hover {
    background-color: ${theme.colors.black};
    opacity: 1;
  }

  &:focus {
    background-color: ${theme.colors.black};
    opacity: 1;
  }
`;

const CustomDropdownContainer = styled.div`
  display: flex;
  gap: 5px;
  position: relative;
  align-items: end;
`;
